import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  Switch,
} from '@mui/material';
import {
  useMiseProductGroupTypes,
  useUpdateAccountSettings,
  useMiseAggregationPeriods,
  useMiseFirstLevelProductGroups,
} from '@/lib/query/hooks/useMiseAccounts';
import type { AccountSettings } from '@/lib/types/mise/account';
import { ProductGroupTypeMultiSelect } from './ProductGroupTypeMultiSelect';
import { buildSelection, splitSelection } from './hub-product-group-types';

/** ランキングソート順の選択肢 */
const RANKING_SORT_OPTIONS = [
  { value: 'sales_quantity', label: '販売数量' },
  { value: 'sales_amount', label: '販売金額' },
] as const;

/** 有効な集計期間の選択肢 */
const PERIOD_OPTIONS = [
  { value: 'CurrentDay', label: '前日' },
  { value: 'CurrentPreviousWeek', label: '前週' },
  { value: 'CurrentThisWeek', label: '当週' },
  { value: 'CurrentMonth', label: '今月' },
  { value: 'CurrentPreviousMonth', label: '前月' },
] as const;

const DEFAULT_ENABLED_PERIODS = ['CurrentDay', 'CurrentPreviousWeek', 'CurrentThisWeek'];
const MAX_ENABLED_PERIODS = 3;
const MAX_ACTION_TARGET_PRODUCT_GROUPS = 5;

interface AccountSettingsEditDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
  settings: AccountSettings | null;
}

/**
 * アカウント設定（バッチ集計条件 5 項目以外）を編集するダイアログ。
 *
 * 非稼働集計日数・欠品しきい値 3 種・併売集計日数の 5 項目は
 * `AccountSettingsNextEditDialog` から next テーブルに書き込む（翌日バッチ反映）。
 */
export function AccountSettingsEditDialog({
  open,
  onClose,
  accountId,
  settings,
}: AccountSettingsEditDialogProps) {
  // 先頭 = 既定タイプ（product_group_type_id）、2 つ目以降 = Hub 用（hub_product_group_type_ids）
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[]>([]);
  const selectedProductGroupTypeId = selectedTypeIds[0] ?? '';
  const [defaultRankingSort, setDefaultRankingSort] = useState<string>('sales_quantity');
  const [enabledPeriods, setEnabledPeriods] = useState<string[]>(DEFAULT_ENABLED_PERIODS);
  const [enabledPeriodsError, setEnabledPeriodsError] = useState<string>('');
  const [summaryPeriod, setSummaryPeriod] = useState<string>('CurrentThisWeek');
  const [productDetailAggregationPeriod, setProductDetailAggregationPeriod] = useState<string>('');
  const [inactiveStockAggregationPeriod, setInactiveStockAggregationPeriod] = useState<string>('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [actionTargetProductGroupIds, setActionTargetProductGroupIds] = useState<string[]>([]);
  const [llmActionUseId, setLlmActionUseId] = useState<boolean>(false);

  const {
    data: productGroupTypes,
    isLoading: productGroupTypesLoading,
    error: productGroupTypesError,
  } = useMiseProductGroupTypes(accountId);

  const {
    data: aggregationPeriods,
    isLoading: aggregationPeriodsLoading,
    error: aggregationPeriodsError,
  } = useMiseAggregationPeriods(accountId);

  const {
    data: firstLevelProductGroups,
    isLoading: firstLevelProductGroupsLoading,
    error: firstLevelProductGroupsError,
  } = useMiseFirstLevelProductGroups(accountId);

  // 更新Mutation
  const updateMutation = useUpdateAccountSettings(accountId);

  // 初期値の設定
  useEffect(() => {
    if (settings) {
      setSelectedTypeIds(
        buildSelection(settings.product_group_type_id, settings.hub_product_group_type_ids),
      );
      setDefaultRankingSort(settings.default_ranking_sort || 'sales_quantity');
      setEnabledPeriods(settings.enabled_periods || DEFAULT_ENABLED_PERIODS);
      setEnabledPeriodsError('');
      setSummaryPeriod(settings.summary_period || 'CurrentThisWeek');
      setProductDetailAggregationPeriod(settings.product_detail_aggregation_period || 'ThisWeek');
      setInactiveStockAggregationPeriod(settings.inactive_stock_aggregation_period || 'ThisWeek');
      setActionTargetProductGroupIds(settings.action_target_product_group_ids || []);
      setLlmActionUseId(settings.llm_action_use_id ?? false);
    } else {
      // 設定がない場合はデフォルト値を設定
      setSelectedTypeIds([]);
      setDefaultRankingSort('sales_quantity');
      setEnabledPeriods(DEFAULT_ENABLED_PERIODS);
      setEnabledPeriodsError('');
      setSummaryPeriod('CurrentThisWeek');
      setProductDetailAggregationPeriod('');
      setInactiveStockAggregationPeriod('');
      setActionTargetProductGroupIds([]);
      setLlmActionUseId(false);
    }
  }, [settings, open]);

  // 取得した商品グループ一覧に存在しないIDを除外（削除された商品グループの残留防止）
  useEffect(() => {
    if (firstLevelProductGroups && actionTargetProductGroupIds.length > 0) {
      const validIds = new Set(firstLevelProductGroups.map((pg) => pg.id));
      const filtered = actionTargetProductGroupIds.filter((id) => validIds.has(id));
      if (filtered.length !== actionTargetProductGroupIds.length) {
        setActionTargetProductGroupIds(filtered);
      }
    }
  }, [firstLevelProductGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  // ダイアログを閉じる際に状態をリセット
  const handleClose = () => {
    if (!updateMutation.isPending) {
      setConfirmDialogOpen(false);
      onClose();
    }
  };

  // 有効な集計期間の変更ハンドラー
  const handleEnabledPeriodChange = (period: string, checked: boolean) => {
    const newPeriods = checked
      ? [...enabledPeriods, period]
      : enabledPeriods.filter((p) => p !== period);
    setEnabledPeriods(newPeriods);
    if (newPeriods.length === 0) {
      setEnabledPeriodsError('少なくとも1つの集計期間を選択してください');
    } else {
      setEnabledPeriodsError('');
    }
    // summaryPeriod が新しい enabledPeriods に含まれない場合はリセット
    if (!newPeriods.includes(summaryPeriod)) {
      const firstAvailable = PERIOD_OPTIONS.find((opt) => newPeriods.includes(opt.value));
      if (firstAvailable) {
        setSummaryPeriod(firstAvailable.value);
      }
    }
  };

  // アクション分析対象商品グループの変更ハンドラー
  const handleActionTargetProductGroupChange = (groupId: string, checked: boolean) => {
    setActionTargetProductGroupIds((prev) =>
      checked ? [...prev, groupId] : prev.filter((id) => id !== groupId),
    );
  };

  // 保存ボタンクリック時の処理
  const handleSave = () => {
    // バリデーション
    if (enabledPeriods.length === 0) {
      setEnabledPeriodsError('少なくとも1つの集計期間を選択してください');
      return;
    }

    // 確認ダイアログを表示
    setConfirmDialogOpen(true);
  };

  // 確認ダイアログで確定した場合の処理
  const handleConfirmSave = async () => {
    setConfirmDialogOpen(false);

    const request = {
      product_group_type_id: splitSelection(selectedTypeIds).productGroupTypeId,
      hub_product_group_type_ids: splitSelection(selectedTypeIds).hubProductGroupTypeIds,
      default_ranking_sort: defaultRankingSort,
      enabled_periods: enabledPeriods,
      summary_period: summaryPeriod,
      product_detail_aggregation_period: productDetailAggregationPeriod,
      inactive_stock_aggregation_period: inactiveStockAggregationPeriod,
      action_target_product_group_ids: actionTargetProductGroupIds,
      llm_action_use_id: llmActionUseId,
    };

    try {
      await updateMutation.mutateAsync(request);
      onClose();
    } catch (error) {
      // エラーはmutationのエラーとして表示される
      console.error('Failed to update account settings:', error);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>アカウント設定を編集</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              バッチ集計条件（非稼働集計日数・欠品しきい値・併売集計日数）は「集計前の設定」から編集してください。
            </Alert>
            <Box sx={{ mb: 2 }}>
              {productGroupTypesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : productGroupTypesError ? (
                <Alert severity="error" sx={{ mt: 1 }}>
                  商品グループタイプ一覧の取得に失敗しました
                </Alert>
              ) : (
                <ProductGroupTypeMultiSelect
                  value={selectedTypeIds}
                  types={productGroupTypes ?? []}
                  onChange={(ids) => {
                    // 既定タイプ（先頭）が変わると対象商品グループは別タイプのものになるため選び直させる
                    if (ids[0] !== selectedTypeIds[0]) setActionTargetProductGroupIds([]);
                    setSelectedTypeIds(ids);
                  }}
                />
              )}
            </Box>

            <FormControl component="fieldset" sx={{ mb: 2, display: 'block' }}>
              <FormLabel component="legend">LLMアクション分析 商品識別子</FormLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={llmActionUseId}
                    onChange={(e) => setLlmActionUseId(e.target.checked)}
                  />
                }
                label={llmActionUseId ? '品番ID（product_customer_id）' : '商品名（product_name）'}
              />
              <FormHelperText>
                AIアクション分析のプロンプトで商品を識別する際に使用する項目を選択します
              </FormHelperText>
            </FormControl>

            <TextField
              select
              fullWidth
              label="デフォルトランキングソート順"
              value={defaultRankingSort}
              onChange={(e) => setDefaultRankingSort(e.target.value)}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                select: {
                  native: true,
                },
              }}
              helperText="ランキング画面のデフォルトのソート順を選択してください"
              sx={{ mb: 2 }}
            >
              {RANKING_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>

            <FormControl
              component="fieldset"
              error={!!enabledPeriodsError}
              sx={{ mb: 2, display: 'block' }}
            >
              <FormLabel component="legend">有効な集計期間（最大3つ）</FormLabel>
              <FormGroup>
                {PERIOD_OPTIONS.map((option) => {
                  const isChecked = enabledPeriods.includes(option.value);
                  const isMaxSelected = enabledPeriods.length >= MAX_ENABLED_PERIODS;
                  return (
                    <FormControlLabel
                      key={option.value}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={(e) =>
                            handleEnabledPeriodChange(option.value, e.target.checked)
                          }
                          disabled={!isChecked && isMaxSelected}
                        />
                      }
                      label={option.label}
                    />
                  );
                })}
              </FormGroup>
              <FormHelperText>
                {enabledPeriodsError || '集計期間を最大3つまで選択してください'}
              </FormHelperText>
            </FormControl>

            <TextField
              select
              fullWidth
              label="サマリー集計期間"
              value={summaryPeriod}
              onChange={(e) => setSummaryPeriod(e.target.value)}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                select: {
                  native: true,
                },
              }}
              helperText="サマリー分析で参照する集計期間を選択してください"
              sx={{ mb: 2 }}
            >
              {PERIOD_OPTIONS.filter((opt) => enabledPeriods.includes(opt.value)).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>

            {aggregationPeriodsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : aggregationPeriodsError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                集計期間選択肢の取得に失敗しました
              </Alert>
            ) : (
              <>
                <TextField
                  select
                  fullWidth
                  label="商品詳細Athena集計期間"
                  value={productDetailAggregationPeriod}
                  onChange={(e) => setProductDetailAggregationPeriod(e.target.value)}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                    select: {
                      native: true,
                    },
                  }}
                  helperText="商品詳細で使用するAthena集計期間を選択してください"
                  sx={{ mb: 2 }}
                >
                  <option value="">選択してください</option>
                  {aggregationPeriods?.map((period) => (
                    <option key={period} value={period}>
                      {period}
                    </option>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  label="非稼働在庫Athena集計期間"
                  value={inactiveStockAggregationPeriod}
                  onChange={(e) => setInactiveStockAggregationPeriod(e.target.value)}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                    select: {
                      native: true,
                    },
                  }}
                  helperText="活動性低在庫で使用するAthena集計期間を選択してください"
                  sx={{ mb: 2 }}
                >
                  <option value="">選択してください</option>
                  {aggregationPeriods?.map((period) => (
                    <option key={period} value={period}>
                      {period}
                    </option>
                  ))}
                </TextField>
              </>
            )}

            {selectedProductGroupTypeId && (
              <FormControl component="fieldset" sx={{ mb: 2, display: 'block' }}>
                <FormLabel component="legend">
                  アクション分析 対象商品グループ（最大{MAX_ACTION_TARGET_PRODUCT_GROUPS}つ）
                </FormLabel>
                {selectedProductGroupTypeId !== (settings?.product_group_type_id ?? '') ? (
                  <FormHelperText>
                    商品グループタイプを変更しました。保存後にダイアログを再度開いて対象商品グループを設定してください。
                  </FormHelperText>
                ) : firstLevelProductGroupsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : firstLevelProductGroupsError ? (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    商品グループ一覧の取得に失敗しました
                  </Alert>
                ) : (
                  <>
                    <FormGroup>
                      {firstLevelProductGroups?.map((pg) => {
                        const isChecked = actionTargetProductGroupIds.includes(pg.id);
                        const isMaxSelected =
                          actionTargetProductGroupIds.length >= MAX_ACTION_TARGET_PRODUCT_GROUPS;
                        return (
                          <FormControlLabel
                            key={pg.id}
                            control={
                              <Checkbox
                                checked={isChecked}
                                onChange={(e) =>
                                  handleActionTargetProductGroupChange(pg.id, e.target.checked)
                                }
                                disabled={!isChecked && isMaxSelected}
                              />
                            }
                            label={pg.name || pg.id}
                          />
                        );
                      })}
                    </FormGroup>
                    <FormHelperText>
                      選択した商品グループごとにAIアクション提案を生成します（最大
                      {MAX_ACTION_TARGET_PRODUCT_GROUPS}つ）。未選択の場合は全商品のみ分析します。
                    </FormHelperText>
                  </>
                )}
              </FormControl>
            )}

            {updateMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : 'アカウント設定の更新に失敗しました'}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={updateMutation.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              updateMutation.isPending ||
              enabledPeriods.length === 0 ||
              !!enabledPeriodsError ||
              !productDetailAggregationPeriod ||
              !inactiveStockAggregationPeriod
            }
          >
            {updateMutation.isPending ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 確認ダイアログ */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            zIndex: 1400, // Material-UIのDialogより高いz-indexを設定
          },
        }}
      >
        <DialogTitle>アカウント設定を更新しますか？</DialogTitle>
        <DialogContent>
          <Typography>この操作により、アカウント設定が更新されます。よろしいですか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={updateMutation.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? '更新中...' : '更新する'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
